import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES } from 'constants/dataSets';
import { COMMENT_TASK } from 'constants/formFields';
import { TASK_ACTIVITIES_CATEGORIES, TASK_TABS } from 'constants/general';
import {
  useCreateCommentTaskMutation,
  useDeleteCommentTaskMutation,
  useDeleteTaskMutation, useGetAttachmentsQuery, useGetCommentsTasksQuery, useGetSingleTaskQuery,
  useGetTasksQuery, useUpdateStatusTaskMutation,
} from 'store/slices/tasksManagement/apis/tasksManagementApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getAccessTaskManagement from 'utils/getAccessTaskManagement';
import {
  getFileNameWithExtension,
  getPriorityIcon,
  getStatusClassName,
  getUserInitials,
} from 'utils/taskHelper';

import useTaskInfoFields from './hooks/useTaskInfoFields';
import TaskInfoView from './TaskInfoView';

import type { TTaskStatus } from 'constants/interfaces';
import type IUser from 'store/slices/user/interfaces/IUser';

import styles from './sass/TaskInfo.module.scss';

function TaskInfo() {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<typeof TASK_TABS[keyof typeof TASK_TABS]>(TASK_TABS.ACTIVITIES);
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [isCreateTaskModalVisible, setIsCreateTaskModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState<TTaskStatus | null>(null);
  const [hideFieldsForm, setHideFieldsForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { taskId = '' } = useParams();
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const { data: singleTask, isLoading: isSingleTaskLoading } = useGetSingleTaskQuery({ id: Number(taskId) });
  const {
    data: attachments,
    isLoading: isAttachmentsLoading,
  } = useGetAttachmentsQuery({ taskId: Number(taskId) });
  const { data: tasksList, isFetching: isTaskListLoading } = useGetTasksQuery({
    offset: 0,
    valueChainMapIds: [singleTask?.data?.valueChainMap?.id || 0],
  });
  const [updateStatusTask] = useUpdateStatusTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  useEffect(() => {
    if (!singleTask && !isSingleTaskLoading && !isUserLoading) {
      navigate(AppRoutes.taskManagement);
    }
  }, [singleTask, isSingleTaskLoading, navigate, isUserLoading]);

  const offset = (currentPage - 1) * DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES;
  const {
    data: activitiesList,
    isFetching: isActivitiesListLoading,
    refetch: refetchActivitiesList,
  } = useGetCommentsTasksQuery({
    offset,
    taskId: Number(taskId),
    category: TASK_ACTIVITIES_CATEGORIES.find((item) => item.label === activeTab)?.value,
  });
  const [createCommentTask] = useCreateCommentTaskMutation();
  const [deleteCommentTask] = useDeleteCommentTaskMutation();

  const { meta, items: activitiesItems } = activitiesList?.data || {};

  const {
    control,
    handleSubmit,
    reset,
  } = useTaskInfoFields();

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleTabChange = (tab: typeof TASK_TABS[keyof typeof TASK_TABS]): void => {
    setActiveTab(tab);
    refetchActivitiesList();
  };

  const handleStatusChange = (id: number, selectedStatus: TTaskStatus): void => {
    setNewStatus(selectedStatus);
    setIsCommentModalVisible(true);
    setCurrentPage(1);
  };

  const onGoToTask = (idTask: string) => {
    navigate(generatePath(AppRoutes.taskManagementId, { taskId: String(idTask) }));
  };

  const onDeleteSwimlaneConfirm = (): void => {
    setIsDeleteModalOpen(false);
    deleteTask({ id: Number(taskId) });
    navigate(generatePath(AppRoutes.taskManagement));
  };

  const onOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const onCommentReject = () => {
    setIsCommentModalVisible(false);
  };

  const onCommentConfirm = (reason: string) => {
    if (newStatus) {
      updateStatusTask({
        id: Number(taskId),
        status: newStatus,
        type: singleTask?.data?.type || '',
        materialityAssessmentId: singleTask?.data?.materialityAssessment?.id,
        valueChainMapId: singleTask?.data?.valueChainMap?.id,
        updateComment: reason,
      });
      setIsCommentModalVisible(false);
    }
  };

  const onCloseModal = () => {
    setIsCreateTaskModalVisible(false);
    setHideFieldsForm(false);
  };

  const onOpenCreateTaskModal = (hideForm?: boolean) => {
    setIsCreateTaskModalVisible(true);
    setHideFieldsForm(hideForm || false);
  };

  const breadcrumbs = [
    {
      label: t('breadcrumbs./task-management'),
      path: AppRoutes.taskManagement,
    },
    {
      label: singleTask?.data?.title || '',
    },
  ];

  const onAddComment = handleSubmit((values) => {
    if (values[COMMENT_TASK]) {
      createCommentTask({ taskId: Number(taskId), comment: values[COMMENT_TASK] });
      reset();
    }
  });

  const onDeleteComment = (id: number) => {
    deleteCommentTask({ id });
  };

  const isLoading = isSingleTaskLoading || isAttachmentsLoading || isTaskListLoading
  || isUserLoading;

  return (
    <TaskInfoView
      isLoading={isLoading}
      data={singleTask?.data}
      onStatusChange={handleStatusChange}
      dataTable={tasksList?.data?.items || []}
      breadcrumbs={breadcrumbs}
      isDeleteModalOpen={isDeleteModalOpen}
      onDeleteSwimlaneConfirm={onDeleteSwimlaneConfirm}
      getPriorityIcon={getPriorityIcon}
      getFileNameWithExtension={getFileNameWithExtension}
      onGoToTask={onGoToTask}
      getStatusClassName={(status: TTaskStatus) => getStatusClassName(status, styles)}
      control={control}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      getUserInitials={getUserInitials}
      attachments={attachments?.data?.items || []}
      isCommentModalVisible={isCommentModalVisible}
      onCommentReject={onCommentReject}
      onCommentConfirm={onCommentConfirm}
      isCreateTaskModalVisible={isCreateTaskModalVisible}
      onCloseModal={onCloseModal}
      onOpenCreateTaskModal={onOpenCreateTaskModal}
      onOpenDeleteModal={onOpenDeleteModal}
      onCloseDeleteModal={onCloseDeleteModal}
      userData={getAccessTaskManagement(userData?.data as IUser)}
      activitiesItems={activitiesItems || []}
      onAddComment={onAddComment}
      onDeleteComment={onDeleteComment}
      isActivitiesListLoading={isActivitiesListLoading}
      newStatus={newStatus}
      hideFieldsForm={hideFieldsForm}
      taskId={taskId}
      currentPage={currentPage}
      totalPages={Math.ceil((meta?.count || 0) / DATA_SET_PAGINATION_COUNT_TASK_ACTIVITIES)}
      onPageChange={setCurrentPage}
    />
  );
}

export default TaskInfo;
